import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@/shared/store/store';
import { Integration, IntegrationFilters, IntegrationStatus } from '../types/integration.types';

// Selector base
const selectIntegrationsState = (state: RootState) => state.integrations;

// Selectores básicos
export const selectIntegrations = createSelector(
  [selectIntegrationsState],
  (state) => state.integrations
);

export const selectCurrentIntegration = createSelector(
  [selectIntegrationsState],
  (state) => state.currentIntegration
);

export const selectIntegrationsLoading = createSelector(
  [selectIntegrationsState],
  (state) => state.loading
);

export const selectIntegrationsError = createSelector(
  [selectIntegrationsState],
  (state) => state.error
);

export const selectIntegrationsPagination = createSelector(
  [selectIntegrationsState],
  (state) => state.pagination
);

export const selectIntegrationsFilters = createSelector(
  [selectIntegrationsState],
  (state) => state.filters
);

export const selectIntegrationsStats = createSelector(
  [selectIntegrationsState],
  (state) => state.stats
);

// Selectores derivados
export const selectIntegrationsByType = createSelector(
  [selectIntegrations],
  (integrations) => {
    const byType: Record<string, Integration[]> = {};
    integrations.forEach((integration) => {
      const type = integration.type;
      if (!byType[type]) {
        byType[type] = [];
      }
      byType[type].push(integration);
    });
    return byType;
  }
);

export const selectIntegrationsByStatus = createSelector(
  [selectIntegrations],
  (integrations) => {
    const byStatus: Record<string, Integration[]> = {};
    integrations.forEach((integration) => {
      const status = integration.status;
      if (!byStatus[status]) {
        byStatus[status] = [];
      }
      byStatus[status].push(integration);
    });
    return byStatus;
  }
);

export const selectActiveIntegrations = createSelector(
  [selectIntegrations],
  (integrations) => integrations.filter((i) => i.isActive && i.status === IntegrationStatus.ACTIVE)
);

export const selectInactiveIntegrations = createSelector(
  [selectIntegrations],
  (integrations) => integrations.filter((i) => !i.isActive || i.status === IntegrationStatus.INACTIVE)
);

export const selectIntegrationsWithErrors = createSelector(
  [selectIntegrations],
  (integrations) => integrations.filter((i) => i.status === IntegrationStatus.ERROR)
);

// Selector para integraciones filtradas
export const selectFilteredIntegrations = createSelector(
  [selectIntegrations, selectIntegrationsFilters],
  (integrations, filters: IntegrationFilters) => {
    let filtered = [...integrations];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (integration) =>
          integration.name.toLowerCase().includes(searchLower) ||
          integration.description?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.type) {
      filtered = filtered.filter((integration) => integration.type === filters.type);
    }

    if (filters.status) {
      filtered = filtered.filter((integration) => integration.status === filters.status);
    }

    if (filters.isActive !== undefined) {
      filtered = filtered.filter((integration) => integration.isActive === filters.isActive);
    }

    return filtered;
  }
);

// Selector para obtener integración por ID
export const selectIntegrationById = (id: string) =>
  createSelector([selectIntegrations], (integrations) =>
    integrations.find((integration) => integration.id === id)
  );

// Selector para estadísticas rápidas
export const selectIntegrationsQuickStats = createSelector(
  [selectIntegrations],
  (integrations) => ({
    total: integrations.length,
    active: integrations.filter((i) => i.status === IntegrationStatus.ACTIVE).length,
    inactive: integrations.filter((i) => i.status === IntegrationStatus.INACTIVE).length,
    error: integrations.filter((i) => i.status === IntegrationStatus.ERROR).length,
    pending: integrations.filter((i) => i.status === IntegrationStatus.PENDING).length,
  })
);

